<template>
  <div class="text-center">
    <v-dialog v-model="visibility" max-width="500" persistent>
      <v-card class="pa-0" flat rounded="lg">
        <!-- Header -->
        <v-toolbar dark color="rgb(206, 45, 45)" class="rounded-t-lg">
          <v-btn icon disabled></v-btn>
          <v-spacer></v-spacer>
          <v-toolbar-title>New Ticket</v-toolbar-title>
          <v-spacer></v-spacer>
          <v-btn icon dark @click="$store.commit('SET_CREATE_TICKET_DIALOG_VISIBILITY', false)">
            <v-icon>mdi-close</v-icon>
          </v-btn>
        </v-toolbar>

        <v-card flat class="ma-0 py-2 px-6">
          <!-- Ticket Info -->
          <v-row no-gutters class="py-1">
            <v-col cols="6" class="px-1">
              <v-select outlined hide-details dense label="Service Types..." v-model="service_type_id" :items="general.service_types" item-text="service_type" item-value="service_type_id"></v-select>
            </v-col>
            <v-col cols="6" class="px-1">
              <v-select outlined hide-details dense label="Dedicated Staffs..." v-model="dedicated_by_user_id" :items="users" :item-text="fullname" item-value="user_id"></v-select>
            </v-col>
          </v-row>
          <v-row no-gutters class="py-1">
            <v-col cols="12" class="px-1">
              <v-select outlined hide-details dense label="Language" v-model="language" :items="language_list"></v-select>
            </v-col>
          </v-row>
          <v-row no-gutters>
            <v-col cols="12" class="px-1 mt-1">
              <v-textarea hide-details dense rows="3" label="Remarks" v-model="remarks" outlined no-resize></v-textarea>
            </v-col>
          </v-row>
          <v-row no-gutters>
            <v-col cols="6" class="mt-n3">
              <v-checkbox dense hide-details label="Online Reservation" v-model="online_reserved" :disabled="isBookingPicked"></v-checkbox>
            </v-col>
          </v-row>

          <!-- Show If the "Online Reservation" checkbox is clicked -->
          <v-slide-x-reverse-transition>
            <v-row no-gutters v-if="online_reserved" class="mt-1">
              <v-col cols="6" class="px-1">
                <v-text-field outlined dense label="Tel" v-model="tel" :disabled="isBookingPicked" :error-messages="createTicketDialog.lookup_error_msg"></v-text-field>
              </v-col>
              <v-col cols="6" class="px-1">
                <v-btn depressed @click="lookUpBooking" :disabled="isBookingPicked">Look Up</v-btn>
              </v-col>
            </v-row>
          </v-slide-x-reverse-transition>

          <!-- Show If the booking record is valid -->
          <v-slide-x-reverse-transition>
            <v-row no-gutters v-if="createTicketDialog.is_booking_valid" class="mt-n4 mb-4">
              <v-col cols="12">
                <div class="text-subtitle-2">Booking Info</div>
                <v-card v-for="record in booking_records" :key="record.booking_id" class="my-3" flat outlined>
                  <v-row class="py-2" justify="center" align="center" no-gutters :class="{ 'colored-outline': booking_id === record.booking_id }">
                    <v-col cols="8">
                      <!-- Window Status -->
                      <v-row no-gutters align="center">
                        <v-col cols="6">
                          <v-card-subtitle class="py-1">Window Status: </v-card-subtitle>
                        </v-col>
                        <v-col cols="6">
                          <div class="py-1 px-4">
                            <v-chip small dark :color="windowStatusColor(record.booking_window_status)">{{ windowStatusLabel(record.booking_window_status) }}</v-chip>
                          </div>
                        </v-col>
                      </v-row>

                      <!-- Already ticketed: the backend refuses a second ticket for this booking, so say so
                           here instead of letting the operator find out from the create error. -->
                      <v-row no-gutters align="center" v-if="isBookingTicketed(record)">
                        <v-col cols="6">
                          <v-card-subtitle class="py-1">Ticket issued: </v-card-subtitle>
                        </v-col>
                        <v-col cols="6">
                          <div class="py-1 px-4">
                            <!-- The chip carries only the code: the column is too narrow for a sentence and v-chip does not wrap -->
                            <v-chip small dark color="grey">{{ record.ticket_code || 'N/A' }}</v-chip>
                          </div>
                        </v-col>
                      </v-row>

                      <!-- Booking Window -->
                      <v-row no-gutters v-if="record.booking_window_start && record.booking_window_end">
                        <v-col cols="6">
                          <v-card-subtitle class="py-1">Window: </v-card-subtitle>
                        </v-col>
                        <v-col cols="6">
                          <v-card-text class="py-1"> {{ formatDateTime(record.booking_window_start) }} - {{ formatDateTime(record.booking_window_end) }} </v-card-text>
                        </v-col>
                      </v-row>

                      <!-- Service Type -->
                      <v-row no-gutters>
                        <v-col cols="6">
                          <v-card-subtitle class="py-1">Service Type: </v-card-subtitle>
                        </v-col>
                        <v-col cols="6">
                          <v-card-text class="py-1"> {{ getServiceTypeByID(record.service_type_id) }} </v-card-text>
                        </v-col>
                      </v-row>

                      <!-- Enquiry Option (derived by the backend from the booking's CS Center) -->
                      <v-row no-gutters>
                        <v-col cols="6">
                          <v-card-subtitle class="py-1">Enquiry Option: </v-card-subtitle>
                        </v-col>
                        <v-col cols="6">
                          <v-card-text class="py-1"> {{ record.enquiry_option || 'N/A' }} </v-card-text>
                        </v-col>
                      </v-row>

                      <!-- Customer Name -->
                      <v-row no-gutters>
                        <v-col cols="6">
                          <v-card-subtitle class="py-1">Customer Name: </v-card-subtitle>
                        </v-col>
                        <v-col cols="6">
                          <v-card-text class="py-1"> {{ record.lastname }} </v-card-text>
                        </v-col>
                      </v-row>

                      <!-- Contact Number (area code + phone number, shown as the backend returns them) -->
                      <v-row no-gutters>
                        <v-col cols="6">
                          <v-card-subtitle class="py-1">Contact Number: </v-card-subtitle>
                        </v-col>
                        <v-col cols="6">
                          <v-card-text class="py-1"> {{ bookingContactNumber(record) }} </v-card-text>
                        </v-col>
                      </v-row>

                      <!-- Dedicated Staff -->
                      <v-row no-gutters>
                        <v-col cols="6">
                          <v-card-subtitle class="py-1">Dedicated Staff: </v-card-subtitle>
                        </v-col>
                        <v-col cols="6">
                          <v-card-text class="py-1"> {{ getStaffNameByID(record.dedicated_staff_id) }} </v-card-text>
                        </v-col>
                      </v-row>

                      <!-- Date -->
                      <v-row no-gutters>
                        <v-col cols="6">
                          <v-card-subtitle class="py-1">Date: </v-card-subtitle>
                        </v-col>
                        <v-col cols="6">
                          <v-card-text class="py-1"> {{ record.date }} </v-card-text>
                        </v-col>
                      </v-row>

                      <!-- Booked Timeslot, e.g. 14:30 - 15:00 (end time computed by the backend) -->
                      <v-row no-gutters>
                        <v-col cols="6">
                          <v-card-subtitle class="py-1">Booked Timeslot: </v-card-subtitle>
                        </v-col>
                        <v-col cols="6">
                          <v-card-text class="py-1"> {{ bookingTimeslot(record) }} </v-card-text>
                        </v-col>
                      </v-row>
                    </v-col>
                    <v-col cols="4">
                      <v-btn style="float: right" class="mr-4" @click="selectBooking(record)" :disabled="booking_id === record.booking_id || isBookingTicketed(record)">SELECT</v-btn>
                    </v-col>
                  </v-row>
                </v-card>

                <!-- What will be created for the selected booking -->
                <v-alert v-if="selectedBooking" dense text class="mb-2" :type="isBookingTicket ? 'success' : 'warning'">
                  Will create: <strong>{{ isBookingTicket ? 'Booking ticket' : 'Walk-in ticket' }}</strong>
                </v-alert>

                <!-- The lookup spans the adjacent booking dates, so say so when the picked record is not today's -->
                <v-alert v-if="selectedBookingDate" dense text type="warning" class="mb-2">
                  This booking is dated <strong>{{ selectedBookingDate }}</strong
                  >, not today ({{ todayInHongKong }}).
                </v-alert>

                <!-- Out-of-window booking without fallback: require operator to pick a walk-in service type -->
                <v-alert v-if="requiresWalkInServiceType" dense text type="info" class="mb-2">
                  This booking is outside its time window and has no fallback service type. Please select a walk-in service type above before creating.
                </v-alert>
              </v-col>
            </v-row>
          </v-slide-x-reverse-transition>
          <!-- Action Button -->
          <v-row no-gutters class="pb-4 mt-2 px-1" :class="{ 'mt-n4': online_reserved }">
            <v-col cols="12">
              <v-btn block @click="createTicket">Create</v-btn>
            </v-col>
          </v-row>
        </v-card>
      </v-card>
    </v-dialog>
  </div>
</template>

<script>
import { mapActions, mapMutations, mapState } from 'vuex'
import bookingStatus from '@/mixins/bookingStatus'
import { bookingLookupDateRange, hongKongDate } from '@/utils/hongKongDate'
export default {
  name: 'createTicketDialog',
  mixins: [bookingStatus],
  data() {
    return {
      language_list: ['Cantonese', 'Mandarin', 'English'],
      language: 'Cantonese',
      dedicated_by_user_id: null,
      remarks: null,
      online_reserved: false,
      reserved_time: null,
      booking_id: null,
      tel: null
    }
  },
  computed: {
    ...mapState(['auth', 'general', 'createTicketDialog']),
    visibility() {
      return this.createTicketDialog.visibility
    },
    service_type_id: {
      get() {
        return this.createTicketDialog.service_type_id
      },
      set(value) {
        this.$store.commit('SET_CREATE_TICKET_DIALOG_SERVICE_TYPE_ID', value)
      }
    },
    booking_records() {
      return this.createTicketDialog.booking_records
    },
    users() {
      return this.general.users
    },
    // The currently selected booking record (if any)
    selectedBooking() {
      if (!this.booking_id) return null
      return this.booking_records.find(record => record.booking_id === this.booking_id) || null
    },
    // Backend decides booking vs walk-in; we only reflect the computed window status as a hint
    isBookingTicket() {
      return this.selectedBooking ? this.selectedBooking.effective_ticket_decision === 'booking' : false
    },
    // The lookup deliberately spans yesterday and tomorrow so a booking whose eligibility window
    // crosses midnight is still findable, and the date range used to be the only thing keeping
    // other days' bookings out of this dialog. Nothing downstream filters by date — `selectBooking`
    // pre-fills from whatever row is picked and `createTicket` posts its `booking_id` — so a record
    // from an adjacent day would otherwise look exactly like today's. Surface the date instead of
    // silently accepting it; the backend still owns the booking-vs-walk-in decision.
    todayInHongKong() {
      return hongKongDate()
    },
    // The selected booking's date when it is NOT today's Hong Kong date, else null.
    selectedBookingDate() {
      if (!this.selectedBooking || !this.selectedBooking.date) return null
      const booking_date = String(this.selectedBooking.date).slice(0, 10)
      return booking_date === this.todayInHongKong ? null : booking_date
    },
    // The TEL and Look Up controls lock once the operator PICKS a booking, not as soon as the
    // lookup returns anything. Locking on "the response was non-empty" meant a lookup that matched
    // only an adjacent-day record froze the dialog on a booking nobody chose, with no way to
    // correct the number short of closing the dialog.
    isBookingPicked() {
      return Boolean(this.booking_id)
    },
    // Out-of-window booking with no fallback service type -> operator must pick a walk-in service type
    requiresWalkInServiceType() {
      if (!this.selectedBooking) return false
      const status = this.selectedBooking.booking_window_status
      const outOfWindow = status === 'early' || status === 'late'
      return outOfWindow && !this.selectedBooking.fallback_service_type_id
    }
  },
  methods: {
    ...mapMutations(['SET_SNACKBAR']),
    ...mapActions(['checkBooking', 'createNormalTicket']),
    fullname: item => item.firstname + ' ' + item.lastname,
    getServiceTypeByID(service_type_id) {
      const service = this.general.service_types.find(service_type => service_type.service_type_id === service_type_id)
      return service ? service.service_type : 'N/A'
    },
    lookUpBooking() {
      if (this.tel) {
        // Hong Kong calendar dates spanning today and both neighbours (see
        // `src/utils/hongKongDate.js`). The bounds must not be derived from `toISOString()`, which
        // is the UTC date and hid every same-day booking from an operator searching between 00:00
        // and 07:59 HKT; the adjacent days keep a booking whose window crosses midnight in range.
        const { from_date, to_date } = bookingLookupDateRange()
        this.checkBooking({
          params: {
            tel: this.tel,
            from_date,
            to_date
            // eligible_at omitted on purpose: the backend uses its own Hong Kong server time,
            // avoiding a wrong window when the operator's browser is not in HK timezone.
          }
        })
      } else {
        this.SET_SNACKBAR({ status: true, color: 'error', text: 'Please enter the TEL for checking the booking record', icon: 'close', timeout: '2000' })
      }
    },
    createTicket() {
      // Booking-driven creation: a booking has been selected after a valid lookup
      if (this.online_reserved) {
        if (!this.createTicketDialog.is_booking_valid || !this.selectedBooking) {
          this.SET_SNACKBAR({ status: true, color: 'error', text: 'Failed to create ticket since the booking information is not valid', icon: 'close', timeout: '2000' })
          return
        }
        // Out-of-window booking with no fallback requires the operator to pick a walk-in
        // service type — and it must be a non-booking service, otherwise the backend rejects
        // it (a booking-enabled service can't be a walk-in target).
        if (this.requiresWalkInServiceType) {
          const selected = this.general.service_types.find(s => s.service_type_id === this.service_type_id)
          if (!this.service_type_id || (selected && Number(selected.booking_enabled) === 1)) {
            this.SET_SNACKBAR({ status: true, color: 'error', text: 'This booking is outside its window. Please select a non-booking (walk-in) service type.', icon: 'close', timeout: '3000' })
            return
          }
        }
        // Backend is authoritative: it recomputes the window and decides booking vs walk-in.
        // We send booking_id plus service_type_id as a fallback hint.
        // NOTE: `is_reserved_ticket` is deliberately NOT sent. The backend derives it from the
        // booking link alone and ignores the request field, so sending it was inert — and leaving
        // it in would read as if the panel still had a say in the queue-priority decision.
        // NOTE: do NOT send `this.tel` — it is the last-4-digit booking lookup value, not a
        // real phone number. Populating tickets.tel would make check_sms attempt an SMS to a
        // partial number. tickets.tel is reserved for genuine SMS-destination phone numbers.
        this.createNormalTicket({
          params: {
            ticket_type: 'normal',
            booking_id: this.booking_id,
            service_type_id: this.service_type_id,
            create_by_user_id: this.auth.user_info.user_id,
            remarks: this.remarks,
            dedicated_by_user_id: this.dedicated_by_user_id,
            reserved_time: this.reserved_time,
            language: this.language
          }
        })
        return
      }

      // Ordinary (non-booking) ticket creation, unchanged behavior
      if (this.service_type_id) {
        // Staff may create manual RA/RC/RM priority tickets (Backend Issue #9).
        // Other booking-enabled services still require a booking lookup. The backend
        // validates staff identity and derives priority; the client sends no priority flag.
        const selected_service = this.general.service_types.find(s => s.service_type_id === this.service_type_id)
        if (selected_service && Number(selected_service.booking_enabled) === 1 && !['RA', 'RC', 'RM'].includes(selected_service.prefix)) {
          this.SET_SNACKBAR({
            status: true,
            color: 'error',
            text: 'This is a booking service. Tick "Online Reservation" and look up the booking to create its ticket.',
            icon: 'close',
            timeout: '3000'
          })
          return
        }
        this.createNormalTicket({
          params: {
            ticket_type: 'normal',
            service_type_id: this.service_type_id,
            create_by_user_id: this.auth.user_info.user_id,
            remarks: this.remarks,
            // The backend grants manual RA/RC/RM priority after checking staff authority.
            dedicated_by_user_id: this.dedicated_by_user_id,
            reserved_time: this.reserved_time,
            language: this.language
          }
        })
      } else {
        this.SET_SNACKBAR({ status: true, color: 'error', text: 'Please select a service type for the ticket', icon: 'close', timeout: '2000' })
      }
    },
    selectBooking(booking_record) {
      // The card's SELECT is disabled for a ticketed booking; this guard covers every other way in
      // (a stale card, a re-used lookup) so the refusal is worded here rather than by the backend.
      if (this.isBookingTicketed(booking_record)) {
        this.SET_SNACKBAR({
          status: true,
          color: 'error',
          text: `${this.bookingTicketLabel(booking_record)} — this booking has been used. Select the other booking, or create a walk-in ticket.`,
          icon: 'close',
          timeout: '3000'
        })
        return
      }
      this.booking_id = booking_record.booking_id
      // When the booking is out of window with no configured fallback, the operator MUST
      // explicitly choose a walk-in service type, so leave it empty (don't pre-fill with the
      // reserved service type). Otherwise pre-fill the effective/fallback service type, which
      // the backend uses as the fallback when out-of-window.
      const status = booking_record.booking_window_status
      const out_of_window_no_fallback = (status === 'early' || status === 'late') && !booking_record.fallback_service_type_id
      this.service_type_id = out_of_window_no_fallback ? null : booking_record.effective_service_type_id || booking_record.fallback_service_type_id || booking_record.service_type_id
      this.dedicated_by_user_id = booking_record.dedicated_staff_id
      this.reserved_time = booking_record.time
    }
  },
  watch: {
    visibility() {
      // Reset the dialog after closed
      if (!this.visibility) {
        this.service_type_id = null
        this.dedicated_by_user_id = null
        this.remarks = null
        this.online_reserved = false
        this.reserved_time = null
        this.booking_id = null
        this.tel = null
        this.language = 'Cantonese'
      }
    },
    online_reserved() {
      // Booking mode is driven by the online reservation flow, not by a hardcoded service type.
      // Reset the selected booking when leaving booking mode. Also clear the
      // service type and dedicated staff that selectBooking() pre-filled from the
      // chosen booking, so a stale booking service type / staff can't silently
      // carry into the subsequent plain (walk-in) ticket path.
      if (!this.online_reserved) {
        this.booking_id = null
        this.reserved_time = null
        this.service_type_id = null
        this.dedicated_by_user_id = null
      }
    }
  }
}
</script>

<style scoped>
.textBox {
  width: 100%;
  border: 1px solid #ccc;
  border-radius: 6px;
  padding: 1px 6px 1px 6px;
  user-select: none;
}

.colored-outline {
  border: 1px solid red;
}
</style>
